<script>
  import EntitySearch  from '../components/EntitySearch.svelte';
  import DatePicker    from '../components/DatePicker.svelte';
  import NewArtistForm from '../components/NewArtistForm.svelte';
  import NewVenueForm  from '../components/NewVenueForm.svelte';
  import { apiFetch }  from '../lib/auth.js';

  // ── Records ────────────────────────────────────────────────────────
  export let records = [];

  // ── Step state: 'list' | 'details' | 'lineup' ────────────────────
  let step    = 'list';
  let editing = null;

  // ── Step 1 — Event details ────────────────────────────────────────
  let formError   = '';
  let formSuccess = false;
  let formLoad    = false;

  let form = blankForm();

  function blankForm() {
    return { name: '', event_type: 'concert', date_start: '', date_end: '', event_venue: null };
  }

  function openAdd() {
    form    = blankForm();
    editing = null;
    step    = 'details';
    formError = ''; formSuccess = false;
    closeNewVenueForm();
  }

  function openEdit(record) {
    form = {
      name:        record.name       || '',
      event_type:  record.event_type || 'concert',
      date_start:  record.date_start || '',
      date_end:    record.date_end   || '',
      event_venue: record.venue_id
        ? { id: record.venue_id, name: record.venue_name || `Venue #${record.venue_id}` }
        : null,
    };
    editing = record;
    step    = 'details';
    formError = ''; formSuccess = false;
    closeNewVenueForm();
    setTimeout(() => document.querySelector('.form-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function closeForm() {
    step    = 'list';
    editing = null;
    formError = ''; formSuccess = false;
    closeNewVenueForm();
    resetLineup();
  }

  async function submitDetails() {
    if (!form.name.trim()) { formError = 'Name is required'; return; }
    if (!form.date_start)  { formError = 'Start date is required'; return; }
    formError = ''; formLoad = true;
    const isEdit = !!editing;
    const endpoint = isEdit ? `/events/${editing.id}` : '/events';
    const body = {
      name: form.name.trim(), event_type: form.event_type,
      date_start: form.date_start, date_end: form.date_end || form.date_start,
    };
    if (form.event_venue) body.venue_ids = [form.event_venue.id];
    try {
      const result = await apiFetch(endpoint, { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(body) });
      records = isEdit
        ? records.map(r => r.id === result.id ? result : r)
        : [result, ...records];
      editing = result;
      await loadPerformances(result.id);
      step = 'lineup';
    } catch (e) {
      formError = e.message || 'Failed to save';
    } finally {
      formLoad = false;
    }
  }

  // ── Delete event ──────────────────────────────────────────────────
  let deleteLoad  = false;
  let deleteError = '';

  async function deleteRecord() {
    if (!editing) return;
    deleteError = ''; deleteLoad = true;
    try {
      await apiFetch(`/events/${editing.id}`, { method: 'DELETE' });
      records = records.filter(r => r.id !== editing.id);
      closeForm();
    } catch (e) {
      deleteError = e.message || 'Failed to delete — please try again.';
    } finally {
      deleteLoad = false;
    }
  }

  // ── Inline new-venue creation ─────────────────────────────────────
  let showNewVenueForm = false;
  let newVenueError    = '';
  let newVenueName     = '';

  function openNewVenueForm(suggestedName = '') {
    newVenueName = suggestedName;
    newVenueError = ''; showNewVenueForm = true;
  }
  function closeNewVenueForm() {
    showNewVenueForm = false;
    newVenueName = ''; newVenueError = '';
  }
  async function submitNewVenue({ detail: payload }) {
    try {
      const created = await apiFetch('/venues', { method: 'POST', body: JSON.stringify(payload) });
      form.event_venue = created;
      closeNewVenueForm();
    } catch (e) {
      newVenueError = e.message || 'Failed to create venue';
    }
  }

  // ── Step 2 — Lineup ───────────────────────────────────────────────
  const MINUTES = ['00', '15', '30', '45'];
  const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

  let performances = [];
  let perfLoading  = false;
  let perfError    = '';

  async function loadPerformances(eventId) {
    perfLoading = true; perfError = '';
    try {
      const data = await apiFetch(`/events/${eventId}/performances`);
      performances = Array.isArray(data) ? data : [];
    } catch (e) {
      perfError = e.message || 'Failed to load lineup';
    } finally {
      perfLoading = false;
    }
  }

  function resetLineup() {
    performances = []; perfError = '';
    closeAddPerfForm(); closeEditPerf();
    showCreateArtist = false;
  }

  // ── Add performance form ──────────────────────────────────────────
  let showAddPerfForm = false;
  let addArtist       = null;
  let addVenue        = null;
  let addDateIso      = '';
  let addStartHH = ''; let addStartMM = '';
  let addEndHH   = ''; let addEndMM   = '';
  let addPerfLoad  = false;
  let addPerfError = '';

  function openAddPerfForm() {
    addArtist = null; addVenue = null;
    addDateIso = ''; addStartHH = addStartMM = addEndHH = addEndMM = '';
    addPerfError = ''; showAddPerfForm = true;
    closeEditPerf();
  }
  function closeAddPerfForm() { showAddPerfForm = false; }

  async function submitAddPerf() {
    if (!addArtist)  { addPerfError = 'Select an artist'; return; }
    if (!addDateIso) { addPerfError = 'Pick a date'; return; }
    addPerfLoad = true; addPerfError = '';
    try {
      const body = { artist_id: addArtist.id, event_id: editing.id, performance_date: addDateIso };
      if (addVenue) body.venue_id = addVenue.id;
      const st = (addStartHH && addStartMM) ? `${addStartHH}:${addStartMM}:00` : null;
      const et = (addEndHH   && addEndMM)   ? `${addEndHH}:${addEndMM}:00`     : null;
      if (st) body.start_time = st;
      if (et) body.end_time   = et;
      await apiFetch('/performances', { method: 'POST', body: JSON.stringify(body) });
      closeAddPerfForm();
      await loadPerformances(editing.id);
    } catch (e) {
      addPerfError = e.message || 'Failed to add';
    } finally {
      addPerfLoad = false;
    }
  }

  // ── Edit performance ──────────────────────────────────────────────
  let editPerf      = null;
  let editArtist    = null;
  let editVenue     = null;
  let editDateIso   = '';
  let editStartHH  = ''; let editStartMM = '';
  let editEndHH    = ''; let editEndMM   = '';
  let editPerfLoad  = false;
  let editPerfError = '';
  let unlinkLoad    = false;
  let unlinkError   = '';

  function openEditPerf(p) {
    editPerf      = p;
    editArtist    = p.artist_id ? { id: p.artist_id, name: p.artist_name } : null;
    editVenue     = p.venue_id  ? { id: p.venue_id,  name: p.venue_name  } : null;
    editDateIso   = p.performance_date || '';
    editStartHH   = p.start_time ? p.start_time.slice(0,2) : '';
    editStartMM   = p.start_time ? p.start_time.slice(3,5) : '';
    editEndHH     = p.end_time   ? p.end_time.slice(0,2)   : '';
    editEndMM     = p.end_time   ? p.end_time.slice(3,5)   : '';
    editPerfError = ''; unlinkError = '';
    closeAddPerfForm();
  }
  function closeEditPerf() { editPerf = null; unlinkError = ''; }

  async function submitEditPerf() {
    if (!editArtist)  { editPerfError = 'Select an artist'; return; }
    if (!editDateIso) { editPerfError = 'Pick a date'; return; }
    editPerfLoad = true; editPerfError = '';
    try {
      const body = { artist_id: editArtist.id, performance_date: editDateIso };
      if (editVenue !== null) body.venue_id = editVenue?.id ?? null;
      const st = (editStartHH && editStartMM) ? `${editStartHH}:${editStartMM}:00` : null;
      const et = (editEndHH   && editEndMM)   ? `${editEndHH}:${editEndMM}:00`     : null;
      if (st) body.start_time = st;
      if (et) body.end_time   = et;
      await apiFetch(`/performances/${editPerf.id}`, { method: 'PUT', body: JSON.stringify(body) });
      closeEditPerf();
      await loadPerformances(editing.id);
    } catch (e) {
      editPerfError = e.message || 'Failed to save';
    } finally {
      editPerfLoad = false;
    }
  }

  async function deletePerf(p) {
    if (!confirm(`Remove ${p.artist_name} from this lineup?`)) return;
    try {
      await apiFetch(`/performances/${p.id}`, { method: 'DELETE' });
      performances = performances.filter(x => x.id !== p.id);
      if (editPerf?.id === p.id) closeEditPerf();
    } catch {}
  }

  async function unlinkPerf(p) {
    unlinkLoad = true; unlinkError = '';
    try {
      await apiFetch(`/performances/${p.id}`, {
        method: 'PUT',
        body: JSON.stringify({ event_id: 0 }),
      });
      closeEditPerf();
      await loadPerformances(editing.id);
    } catch (e) {
      unlinkError = e.message || 'Failed to unlink';
    } finally {
      unlinkLoad = false;
    }
  }

  // ── Link existing quick_log performance ───────────────────────────
  let showLinkPanel = false;
  let linkSearch    = '';
  let linkResults   = [];
  let linkLoading   = false;
  let linkError     = '';
  let linkingId     = null;
  let linkDebounce;

  function openLinkPanel() {
    showLinkPanel = true; linkSearch = ''; linkResults = [];
    linkError = ''; linkingId = null;
    closeAddPerfForm(); closeEditPerf();
  }
  function closeLinkPanel() { showLinkPanel = false; }

  async function searchQuickLog() {
    linkLoading = true; linkError = '';
    try {
      const params = new URLSearchParams();
      if (linkSearch.trim())   params.set('artist',    linkSearch.trim());
      if (editing?.date_start) params.set('date_from', editing.date_start);
      if (editing?.date_end)   params.set('date_to',   editing.date_end);
      const data = await apiFetch(`/performances/quick-log?${params}`);
      linkResults = Array.isArray(data) ? data : [];
    } catch (e) {
      linkError = e.message || 'Search failed';
    } finally {
      linkLoading = false;
    }
  }

  function onLinkSearchInput() {
    clearTimeout(linkDebounce);
    linkDebounce = setTimeout(searchQuickLog, 300);
  }

  async function linkPerformance(p) {
    linkingId = p.id;
    const sourceEventId = p.event_id;
    try {
      await apiFetch(`/performances/${p.id}`, {
        method: 'PUT',
        body: JSON.stringify({ event_id: editing.id }),
      });

      // Clean up the source quick_log event if now empty
      try {
        const remaining = await apiFetch(`/events/${sourceEventId}/performances`);
        if (Array.isArray(remaining) && remaining.length === 0) {
          await apiFetch(`/events/${sourceEventId}`, { method: 'DELETE' });
        }
      } catch { /* non-fatal */ }

      linkResults = linkResults.filter(r => r.id !== p.id);
      await loadPerformances(editing.id);
    } catch (e) {
      linkError = e.message || 'Failed to link performance';
    } finally {
      linkingId = null;
    }
  }

  function formatTime(t) { return t ? t.slice(0, 5) : null; }

  // ── Inline artist create (shared for add + edit perf forms) ───────
  // ── Inline artist create (shared for add + edit perf forms) ───────
  let showCreateArtist = false;
  let createForForm    = '';   // 'add' | 'edit'
  let createArtError   = '';
  let createArtName    = '';   // initial name pre-filled from search query

  function openCreateArtist(q, forForm) {
    createForForm = forForm;
    createArtName = q || '';
    createArtError = ''; showCreateArtist = true;
  }
  function closeCreateArtist() {
    showCreateArtist = false;
    createArtName = ''; createArtError = '';
  }
  async function submitCreateArtist({ detail: payload }) {
    try {
      const created = await apiFetch('/artists', { method: 'POST', body: JSON.stringify(payload) });
      if (createForForm === 'add') addArtist  = created;
      else                         editArtist = created;
      closeCreateArtist();
    } catch (e) {
      createArtError = e.message || 'Failed to create artist';
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────
  function formatDate(d) {
    if (!d) return '—';
    const [y, m, day] = d.split('-');
    return `${day}.${m}.${y}`;
  }
</script>

<!-- ══════════════════════════════════════════════════════════════════
     LIST
     ══════════════════════════════════════════════════════════════════ -->
{#if step === 'list'}
  <div class="list-header">
    <span class="record-count">{records.length} records</span>
    <div class="list-header-actions">
      <button class="btn-add-top" on:click={openAdd}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Add Event
      </button>
    </div>
  </div>

  {#if records.length === 0}
    <div class="state-msg empty">No events yet.</div>
  {:else}
    <div class="list">
      {#each records as record (record.id)}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div class="list-row event-row" role="button" tabindex="0"
          on:click={() => openEdit(record)}
          on:keydown={e => e.key === 'Enter' && openEdit(record)}
        >
          <div class="event-left">
            <span class="row-label">{record.name}</span>
            {#if record.date_start}
              <span class="row-sub">{formatDate(record.date_start)}</span>
            {/if}
          </div>
          <div class="event-right">
            {#if record.event_type}
              <span class="event-badge">{record.event_type}</span>
            {/if}
            <svg class="row-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
        </div>
      {/each}
    </div>
  {/if}

<!-- ══════════════════════════════════════════════════════════════════
     STEP 1 — EVENT DETAILS
     ══════════════════════════════════════════════════════════════════ -->
{:else if step === 'details'}
  <div class="form-panel">
    <div class="form-header">
      <div class="form-header-left">
        <button class="btn-back" on:click={closeForm}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <span>{editing ? 'Edit Event' : 'Add Event'}</span>
      </div>
      <div class="step-indicator">
        <span class="step active">1 Details</span>
        <span class="step-sep">›</span>
        <span class="step">2 Lineup</span>
      </div>
    </div>

    {#if formError}<div class="form-error">{formError}</div>{/if}

    <div class="field">
      <label for="event-name">Event Name *</label>
      <input id="event-name" type="text" bind:value={form.name} placeholder="e.g. Svinesund Deathfest" />
    </div>

    <EntitySearch
      bind:value={form.event_venue}
      label="Venue"
      endpoint="/venues"
      searchParam="name"
      displayField="name"
      subField="city"
      placeholder="Search venue..."
    >
      <svelte:fragment slot="add-new" let:query>
        {#if !showNewVenueForm}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <div
            class="dropdown-suggest"
            role="button" tabindex="0"
            on:click={() => openNewVenueForm(query)}
            on:keydown={e => e.key === 'Enter' && openNewVenueForm(query)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add "{query}" as new venue
          </div>
        {/if}
      </svelte:fragment>
    </EntitySearch>

    {#if showNewVenueForm}
      <NewVenueForm
        initialName={newVenueName}
        error={newVenueError}
        on:submit={submitNewVenue}
        on:cancel={closeNewVenueForm}
      />
    {/if}

    <div class="row">
      <div class="field">
        <label>Start Date *</label>
        <DatePicker bind:value={form.date_start} />
      </div>
      <div class="field">
        <label>End Date</label>
        <DatePicker bind:value={form.date_end} clearable />
      </div>
    </div>

    <div class="field">
      <label for="event-type">Type</label>
      <select id="event-type" bind:value={form.event_type}>
        <option value="concert">Concert</option>
        <option value="festival">Festival</option>
        <option value="quick_log">Quick Log</option>
      </select>
    </div>

    <div class="btn-row">
      <button class="btn-secondary" on:click={closeForm}>Cancel</button>
      {#if editing}
        <button class="btn-danger" on:click={deleteRecord} disabled={deleteLoad}>
          {deleteLoad ? 'DELETING...' : 'DELETE'}
        </button>
      {/if}
      <button class="btn-primary" on:click={submitDetails} disabled={formLoad}>
        {formLoad ? 'SAVING...' : 'SAVE & ADD LINEUP →'}
      </button>
    </div>
    {#if deleteError}<div class="form-error">{deleteError}</div>{/if}
  </div>

<!-- ══════════════════════════════════════════════════════════════════
     STEP 2 — LINEUP
     ══════════════════════════════════════════════════════════════════ -->
{:else if step === 'lineup'}
  <div class="form-panel">
    <div class="form-header">
      <div class="form-header-left">
        <button class="btn-back" on:click={() => { step = 'details'; closeAddPerfForm(); closeEditPerf(); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <span class="header-event-name">{editing?.name}</span>
      </div>
      <div class="step-indicator">
        <span class="step">1 Details</span>
        <span class="step-sep">›</span>
        <span class="step active">2 Lineup</span>
      </div>
    </div>

    <!-- Event meta summary -->
    <div class="event-meta">
      {#if editing?.date_start}
        <span class="meta-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          {formatDate(editing.date_start)}{#if editing.date_end && editing.date_end !== editing.date_start} – {formatDate(editing.date_end)}{/if}
        </span>
      {/if}
      {#if editing?.venue_name || editing?.city}
        <span class="meta-sep">·</span>
        <span class="meta-item">{editing.venue_name || editing.city}</span>
      {/if}
      {#if editing?.event_type}
        <span class="meta-sep">·</span>
        <span class="meta-badge">{editing.event_type}</span>
      {/if}
    </div>

    <!-- Lineup section header -->
    <div class="section-header">
      <span class="section-title">Lineup</span>
      {#if !showAddPerfForm && !editPerf && !showLinkPanel}
        <div class="section-actions">
          <button class="btn-add" on:click={openAddPerfForm}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add Performance
          </button>
          <button class="btn-link" on:click={openLinkPanel}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            Link Existing
          </button>
        </div>
      {/if}
    </div>

    <!-- Add performance form -->
    {#if showAddPerfForm}
      <div class="perf-form">
        <div class="perf-form-header">
          <span>New Performance</span>
          <button class="btn-dismiss" on:click={closeAddPerfForm}>✕</button>
        </div>
        {#if addPerfError}<div class="form-error">{addPerfError}</div>{/if}

        <EntitySearch
          bind:value={addArtist}
          label="Artist *"
          endpoint="/artists"
          searchParam="name"
          displayField="name"
          placeholder="Search artist..."
        >
          <svelte:fragment slot="add-new" let:query>
            {#if !showCreateArtist}
              <div class="add-new-row">
                <button type="button" class="add-new-btn" on:mousedown|preventDefault={() => openCreateArtist(query, 'add')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add "{query}"
                </button>
              </div>
            {/if}
          </svelte:fragment>
        </EntitySearch>

        {#if showCreateArtist && createForForm === 'add'}
          <NewArtistForm
            initialName={createArtName}
            error={createArtError}
            on:submit={submitCreateArtist}
            on:cancel={closeCreateArtist}
          />
        {/if}

        <div class="field">
          <label>Date * <span class="hint">tap to pick</span></label>
          <DatePicker bind:value={addDateIso} clearable />
        </div>

        <div class="field">
          <EntitySearch
            bind:value={addVenue}
            label="Venue"
            endpoint="/venues"
            searchParam="name"
            displayField="name"
            subField="city"
            placeholder="Search venue..."
          />
        </div>

        <div class="time-row">
          <div class="field">
            <span class="field-label">Start <span class="hint">optional</span></span>
            <div class="time-selects">
              <select class="time-select" bind:value={addStartHH}>
                <option value="">HH</option>
                {#each HOURS as h}<option value={h}>{h}</option>{/each}
              </select>
              <span class="time-colon">:</span>
              <select class="time-select" bind:value={addStartMM} disabled={addStartHH === ''}>
                <option value="">MM</option>
                {#each MINUTES as m}<option value={m}>{m}</option>{/each}
              </select>
              {#if addStartHH !== ''}
                <button type="button" class="time-clear" on:click={() => { addStartHH=''; addStartMM=''; }}>✕</button>
              {/if}
            </div>
          </div>
          <div class="field">
            <span class="field-label">End <span class="hint">optional</span></span>
            <div class="time-selects">
              <select class="time-select" bind:value={addEndHH}>
                <option value="">HH</option>
                {#each HOURS as h}<option value={h}>{h}</option>{/each}
              </select>
              <span class="time-colon">:</span>
              <select class="time-select" bind:value={addEndMM} disabled={addEndHH === ''}>
                <option value="">MM</option>
                {#each MINUTES as m}<option value={m}>{m}</option>{/each}
              </select>
              {#if addEndHH !== ''}
                <button type="button" class="time-clear" on:click={() => { addEndHH=''; addEndMM=''; }}>✕</button>
              {/if}
            </div>
          </div>
        </div>

        <div class="btn-row">
          <button class="btn-secondary" on:click={closeAddPerfForm}>Cancel</button>
          <button class="btn-primary" on:click={submitAddPerf} disabled={addPerfLoad}>
            {addPerfLoad ? 'ADDING...' : 'ADD TO LINEUP'}
          </button>
        </div>
      </div>
    {/if}

    <!-- Link existing quick_log performance -->
    {#if showLinkPanel}
      <div class="perf-form">
        <div class="perf-form-header">
          <span>Link Existing Performance</span>
          <button class="btn-dismiss" on:click={closeLinkPanel}>✕</button>
        </div>
        <p class="link-hint">Search for shows logged via quick-log and link them to this event.</p>

        <div class="link-search-wrap">
          <svg class="link-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            class="link-search-input"
            type="text"
            placeholder="Search by artist name…"
            bind:value={linkSearch}
            on:input={onLinkSearchInput}
            on:focus={() => { if (!linkResults.length && !linkLoading) searchQuickLog(); }}
          />
        </div>

        {#if linkError}
          <div class="form-error">{linkError}</div>
        {/if}

        {#if linkLoading}
          <div class="link-state"><span class="spinner"></span> Searching...</div>
        {:else if linkResults.length === 0 && linkSearch.length > 0}
          <div class="link-state dim">No quick-log performances found.</div>
        {:else if linkResults.length > 0}
          <div class="link-results">
            {#each linkResults as p (p.id)}
              <div class="link-row">
                <div class="link-row-info">
                  <span class="link-artist">{p.artist_name}</span>
                  {#if p.artist_country}<span class="link-country">({p.artist_country})</span>{/if}
                  <div class="link-meta">
                    {#if p.performance_date}<span class="link-date">{formatDate(p.performance_date)}</span>{/if}
                    {#if p.start_time}<span class="link-sep">·</span><span class="link-time">{formatTime(p.start_time)}</span>{/if}
                    {#if p.venue_name}<span class="link-sep">·</span><span class="link-venue">{p.venue_name}{#if p.venue_city}, {p.venue_city}{/if}</span>{/if}
                  </div>
                </div>
                <button
                  class="btn-link-row"
                  disabled={linkingId === p.id}
                  on:click={() => linkPerformance(p)}
                >
                  {#if linkingId === p.id}
                    <span class="spinner-sm"></span>
                  {:else}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                    Link
                  {/if}
                </button>
              </div>
            {/each}
          </div>
        {:else}
          <div class="link-state dim">Start typing to search performances.</div>
        {/if}
      </div>
    {/if}

    <!-- Edit performance form -->
    {#if editPerf}
      <div class="perf-form perf-form--edit">
        <div class="perf-form-header">
          <span>Edit · {editPerf.artist_name}</span>
          <button class="btn-dismiss" on:click={closeEditPerf}>✕</button>
        </div>
        {#if editPerfError}<div class="form-error">{editPerfError}</div>{/if}
        {#if unlinkError}<div class="form-error">{unlinkError}</div>{/if}

        <EntitySearch
          bind:value={editArtist}
          label="Artist *"
          endpoint="/artists"
          searchParam="name"
          displayField="name"
          placeholder="Search artist..."
        >
          <svelte:fragment slot="add-new" let:query>
            {#if !showCreateArtist}
              <div class="add-new-row">
                <button type="button" class="add-new-btn" on:mousedown|preventDefault={() => openCreateArtist(query, 'edit')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add "{query}"
                </button>
              </div>
            {/if}
          </svelte:fragment>
        </EntitySearch>

        {#if showCreateArtist && createForForm === 'edit'}
          <NewArtistForm
            initialName={createArtName}
            error={createArtError}
            on:submit={submitCreateArtist}
            on:cancel={closeCreateArtist}
          />
        {/if}

        <div class="field">
          <label>Date * <span class="hint">tap to pick</span></label>
          <DatePicker bind:value={editDateIso} clearable />
        </div>

        <div class="field">
          <EntitySearch
            bind:value={editVenue}
            label="Venue"
            endpoint="/venues"
            searchParam="name"
            displayField="name"
            subField="city"
            placeholder="Search venue..."
          />
        </div>

        <div class="time-row">
          <div class="field">
            <span class="field-label">Start <span class="hint">optional</span></span>
            <div class="time-selects">
              <select class="time-select" bind:value={editStartHH}>
                <option value="">HH</option>
                {#each HOURS as h}<option value={h}>{h}</option>{/each}
              </select>
              <span class="time-colon">:</span>
              <select class="time-select" bind:value={editStartMM} disabled={editStartHH === ''}>
                <option value="">MM</option>
                {#each MINUTES as m}<option value={m}>{m}</option>{/each}
              </select>
              {#if editStartHH !== ''}
                <button type="button" class="time-clear" on:click={() => { editStartHH=''; editStartMM=''; }}>✕</button>
              {/if}
            </div>
          </div>
          <div class="field">
            <span class="field-label">End <span class="hint">optional</span></span>
            <div class="time-selects">
              <select class="time-select" bind:value={editEndHH}>
                <option value="">HH</option>
                {#each HOURS as h}<option value={h}>{h}</option>{/each}
              </select>
              <span class="time-colon">:</span>
              <select class="time-select" bind:value={editEndMM} disabled={editEndHH === ''}>
                <option value="">MM</option>
                {#each MINUTES as m}<option value={m}>{m}</option>{/each}
              </select>
              {#if editEndHH !== ''}
                <button type="button" class="time-clear" on:click={() => { editEndHH=''; editEndMM=''; }}>✕</button>
              {/if}
            </div>
          </div>
        </div>

        <div class="btn-row">
          <button class="btn-danger" on:click={() => deletePerf(editPerf)}>Delete</button>
          <button class="btn-secondary" on:click={closeEditPerf}>Cancel</button>
          <button class="btn-secondary" disabled={unlinkLoad} on:click={() => unlinkPerf(editPerf)}>
            {unlinkLoad ? 'Unlinking...' : 'Unlink'}
          </button>
          <button class="btn-primary" on:click={submitEditPerf} disabled={editPerfLoad}>
            {editPerfLoad ? 'SAVING...' : 'SAVE'}
          </button>
        </div>
      </div>
    {/if}

    <!-- Lineup table -->
    {#if perfLoading}
      <div class="state-loading"><span class="spinner"></span> Loading lineup...</div>
    {:else if perfError}
      <div class="state-error">{perfError}</div>
    {:else if performances.length === 0}
      <div class="empty-lineup">No performances yet — add the first one above.</div>
    {:else}
      <div class="lineup-table-wrap">
        <table class="lineup-table">
          <thead>
            <tr>
              <th>Artist</th><th>Venue</th><th>Date</th><th>Time</th><th></th>
            </tr>
          </thead>
          <tbody>
            {#each performances as p (p.id)}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <tr
                class:editing={editPerf?.id === p.id}
                role="button" tabindex="0"
                on:click={() => editPerf?.id === p.id ? closeEditPerf() : openEditPerf(p)}
                on:keydown={e => e.key === 'Enter' && openEditPerf(p)}
              >
                <td class="td-artist">{p.artist_name || '—'}</td>
                <td class="td-stage">{#if p.venue_name}{p.venue_name}{:else}<span class="dim">—</span>{/if}</td>
                <td class="td-date">{formatDate(p.performance_date)}</td>
                <td class="td-time">
                  {#if p.start_time}
                    {formatTime(p.start_time)}{#if p.end_time} – {formatTime(p.end_time)}{/if}
                  {:else}
                    <span class="dim">—</span>
                  {/if}
                </td>
                <td class="td-edit">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}

    <div class="lineup-footer">
      <button class="btn-secondary" on:click={closeForm}>Done</button>
    </div>
  </div>
{/if}

<style>
  /* ── Step header ──────────────────────────────────────────────────── */
  .form-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.1rem; }
  .form-header-left { display: flex; align-items: center; gap: 0.5rem;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.7rem;
    letter-spacing: 0.2em; text-transform: uppercase; color: #6db800; }

  .btn-back {
    background: none; border: none; color: #3a6b00; cursor: pointer;
    display: flex; align-items: center; padding: 0; transition: color 0.15s;
  }
  .btn-back svg { width: 16px; height: 16px; }
  .btn-back:hover { color: #6db800; }

  .header-event-name { color: #c8c8c8; }

  .step-indicator { display: flex; align-items: center; gap: 0.4rem;
    font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700;
    letter-spacing: 0.2em; text-transform: uppercase; }
  .step { color: #2a5200; }
  .step.active { color: #6db800; }
  .step-sep { color: #1e3d00; }

  /* ── Event meta strip ────────────────────────────────────────────── */
  .event-meta {
    display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem;
    padding: 0.55rem 0.75rem; margin-bottom: 0.85rem;
    background: #0a1200; border: 1px solid #1e3d00; border-radius: 2px;
    font-family: 'Rajdhani', sans-serif;
  }
  .meta-item { display: inline-flex; align-items: center; gap: 0.3rem;
    font-size: 0.75rem; font-weight: 600; color: #6db800; }
  .meta-item svg { width: 12px; height: 12px; stroke: #3a6b00; flex-shrink: 0; }
  .meta-sep { color: #2a5200; }
  .meta-badge { font-size: 0.5rem; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase;
    color: #3a6b00; border: 1px solid #1e3d00; border-radius: 2px; padding: 0.15rem 0.45rem; }

  /* ── Section header ──────────────────────────────────────────────── */
  .section-actions { display: flex; align-items: center; gap: 0.5rem; }
  .btn-link {
    display: inline-flex; align-items: center; gap: 0.3rem;
    background: none; border: 1px solid #1e3d00; color: #2a5200;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem;
    letter-spacing: 0.2em; text-transform: uppercase; padding: 0.4rem 0.7rem;
    border-radius: 2px; cursor: pointer; transition: color 0.15s, border-color 0.15s, background 0.15s;
  }
  .btn-link svg { width: 12px; height: 12px; }
  .btn-link:hover { color: #6db800; border-color: #3a6b00; background: rgba(109,184,0,0.05); }

  /* ── Link panel ──────────────────────────────────────────────────── */
  .link-hint { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; color: #2a5200; letter-spacing: 0.05em; margin-bottom: 0.75rem; }
  .link-search-wrap {
    display: flex; align-items: center; gap: 0.4rem;
    background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    padding: 0.4rem 0.65rem; margin-bottom: 0.75rem;
  }
  .link-search-icon { width: 13px; height: 13px; color: #2a5200; flex-shrink: 0; }
  .link-search-input {
    background: none; border: none; outline: none; width: 100%;
    font-family: 'Rajdhani', sans-serif; font-size: 0.9rem; font-weight: 600; color: #c8c8c8;
  }
  .link-search-input::placeholder { color: #2a5200; }
  .link-state {
    display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 0;
    font-family: 'Rajdhani', sans-serif; font-size: 0.65rem;
    letter-spacing: 0.12em; text-transform: uppercase; color: #2a5200;
  }
  .link-state.dim { color: #1e3d00; }
  .link-results { display: flex; flex-direction: column; border: 1px solid #1e3d00; border-radius: 2px; overflow: hidden; }
  .link-row {
    display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
    padding: 0.65rem 0.75rem; border-bottom: 1px solid #0f1a00; transition: background 0.1s;
  }
  .link-row:last-child { border-bottom: none; }
  .link-row:hover { background: #0a1200; }
  .link-row-info { display: flex; flex-direction: column; gap: 0.15rem; flex: 1; min-width: 0; }
  .link-artist { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.9rem; color: #c8c8c8; }
  .link-country { font-size: 0.8rem; color: #3a6b00; margin-left: 0.25rem; }
  .link-meta { display: flex; align-items: center; gap: 0.3rem; flex-wrap: wrap; }
  .link-date  { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; color: #4a7c00; }
  .link-time  { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; color: #3a6b00; }
  .link-venue { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; color: #555; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .link-sep   { color: #1e3d00; font-size: 0.6rem; }
  .btn-link-row {
    display: inline-flex; align-items: center; gap: 0.25rem; flex-shrink: 0;
    background: #0d1a00; border: 1px solid #1e3d00; border-radius: 2px;
    color: #3a6b00; font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.52rem; letter-spacing: 0.15em; text-transform: uppercase;
    padding: 0.3rem 0.6rem; cursor: pointer;
    transition: color 0.15s, border-color 0.15s, background 0.15s;
  }
  .btn-link-row svg { width: 11px; height: 11px; }
  .btn-link-row:hover:not(:disabled) { color: #6db800; border-color: #3a6b00; background: #122200; }
  .btn-link-row:disabled { opacity: 0.5; cursor: not-allowed; }
  .spinner-sm {
    display: inline-block; width: 10px; height: 10px; flex-shrink: 0;
    border: 1.5px solid #1e3d00; border-top-color: #6db800;
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }

  /* ── Section header ──────────────────────────────────────────────── */
  .section-header { display: flex; align-items: center; justify-content: space-between;
    border-bottom: 1px solid #1e3d00; padding-bottom: 0.5rem; margin-bottom: 0.85rem; }
  .section-title { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem;
    letter-spacing: 0.3em; text-transform: uppercase; color: #3a6b00;
    border-left: 2px solid #3a6b00; padding-left: 0.5rem; }
  .btn-add { display: inline-flex; align-items: center; gap: 0.3rem;
    background: #1a3300; border: 1px solid #3a6b00; color: #6db800;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem;
    letter-spacing: 0.2em; text-transform: uppercase; padding: 0.4rem 0.7rem;
    border-radius: 2px; cursor: pointer; transition: background 0.2s; }
  .btn-add svg { width: 12px; height: 12px; }
  .btn-add:hover { background: #223d00; }

  /* ── Perf forms ──────────────────────────────────────────────────── */
  .perf-form { background: #080f00; border: 1px solid #2a5200; border-radius: 3px;
    padding: 1rem; margin-bottom: 1rem; animation: slideDown 0.15s ease; }
  .perf-form--edit { border-color: #1e5500; }
  @keyframes slideDown { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }

  .perf-form-header { display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 0.9rem; font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.55rem; letter-spacing: 0.3em; text-transform: uppercase; color: #3a6b00; }

  /* ── Time row ────────────────────────────────────────────────────── */
  .time-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
  .time-selects { display: flex; align-items: center; gap: 0.3rem; }
  .time-select { background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #c8c8c8; font-family: 'Rajdhani', sans-serif; font-size: 0.9rem; font-weight: 600;
    padding: 0.5rem 0.3rem; cursor: pointer; outline: none; flex: 1;
    transition: border-color 0.2s; appearance: none; text-align: center; }
  .time-select:focus { border-color: #3a6b00; }
  .time-select:disabled { opacity: 0.35; cursor: not-allowed; }
  .time-select option { background: #0d1a00; }
  .time-colon { color: #3a6b00; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 1.1rem; user-select: none; }
  .time-clear { background: none; border: none; color: #2a5200; cursor: pointer;
    font-size: 0.65rem; padding: 0.2rem; line-height: 1; transition: color 0.15s; flex-shrink: 0; }
  .time-clear:hover { color: #cc4400; }


  /* ── Inline entity create ────────────────────────────────────────── */
  .add-new-row { padding: 0.5rem 0.75rem; border-top: 1px solid #1e3d00; }
  .add-new-btn { display: flex; align-items: center; gap: 0.4rem; width: 100%;
    background: none; border: none; cursor: pointer;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.7rem;
    letter-spacing: 0.1em; color: #3a6b00; padding: 0.2rem 0; transition: color 0.15s; }
  .add-new-btn:hover { color: #6db800; }
  .add-new-btn svg { width: 13px; height: 13px; flex-shrink: 0; }
  /* ── Venue quick-create ──────────────────────────────────────────── */
  .dropdown-suggest { display: flex; align-items: center; gap: 0.5rem;
    padding: 0.65rem 0.75rem; cursor: pointer;
    font-family: 'Rajdhani', sans-serif; font-size: 0.8rem; font-weight: 700;
    letter-spacing: 0.08em; color: #4a7c00;
    border-top: 1px solid #1e3d00; transition: background 0.15s, color 0.15s; }
  .dropdown-suggest svg { width: 13px; height: 13px; flex-shrink: 0; }
  .dropdown-suggest:hover { background: #0d1a00; color: #6db800; }

  /* ── Lineup table ────────────────────────────────────────────────── */
  .lineup-table-wrap { border: 1px solid #1e3d00; border-radius: 2px; overflow-x: auto; margin-bottom: 1rem; }
  .lineup-table { width: 100%; border-collapse: collapse; font-family: 'Rajdhani', sans-serif; }
  .lineup-table thead tr { background: #0a1200; border-bottom: 1px solid #1e3d00; }
  .lineup-table th { padding: 0.5rem 0.75rem; font-size: 0.5rem; font-weight: 700;
    letter-spacing: 0.25em; text-transform: uppercase; color: #3a6b00; text-align: left; white-space: nowrap; }
  .lineup-table tbody tr { border-bottom: 1px solid #0a1200; cursor: pointer; transition: background 0.1s; }
  .lineup-table tbody tr:last-child { border-bottom: none; }
  .lineup-table tbody tr:hover { background: #0a1200; }
  .lineup-table tbody tr.editing { background: #0d1a00; outline: 1px solid #2a5200; outline-offset: -1px; }
  .lineup-table td { padding: 0.6rem 0.75rem; vertical-align: middle; }
  .td-artist { font-size: 0.9rem; font-weight: 600; color: #c8c8c8; white-space: nowrap; }
  .td-stage  { font-size: 0.75rem; color: #4a7c00; white-space: nowrap; }
  .td-date   { font-size: 0.75rem; color: #3a6b00; white-space: nowrap; }
  .td-time   { font-size: 0.75rem; color: #555; white-space: nowrap; }
  .td-edit   { width: 28px; text-align: right; }
  .td-edit svg { width: 13px; height: 13px; stroke: #2a5200; transition: stroke 0.15s; }
  .lineup-table tbody tr:hover .td-edit svg { stroke: #6db800; }
  .dim { color: #333; }

  .empty-lineup { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem;
    letter-spacing: 0.12em; text-transform: uppercase; color: #1e3d00;
    padding: 1.5rem 0; text-align: center; }
  .state-loading { display: flex; align-items: center; gap: 0.6rem;
    font-family: 'Rajdhani', sans-serif; font-size: 0.65rem;
    letter-spacing: 0.15em; text-transform: uppercase; color: #2a5200; padding: 1.5rem 0; }

  .lineup-footer { padding-top: 0.5rem; }

  /* ── Misc shared ─────────────────────────────────────────────────── */
  .field { display: flex; flex-direction: column; gap: 0.35rem; margin-bottom: 0.85rem; }
  .field label, .field .field-label { font-family: 'Rajdhani', sans-serif; font-weight: 600;
    font-size: 0.55rem; letter-spacing: 0.25em; text-transform: uppercase; color: #3a6b00; }
  .hint, .field-hint { font-weight: 400; color: #2a5200; text-transform: none; letter-spacing: 0.05em; font-size: 0.52rem; }

  select { width: 100%; background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #c8c8c8; font-family: 'Rajdhani', sans-serif; font-size: 0.95rem; font-weight: 600;
    padding: 0.55rem 0.65rem; outline: none; transition: border-color 0.2s; appearance: none; cursor: pointer; }
  select:focus { border-color: #3a6b00; }
  select option { background: #0a0a0a; }

  .event-row { justify-content: space-between; }
  .event-left { display: flex; flex-direction: column; gap: 0.1rem; flex: 1; min-width: 0; }
  .event-left .row-label { flex: none; }
  .event-right { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
  .event-badge { font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700;
    letter-spacing: 0.15em; text-transform: uppercase; color: #2a5200;
    border: 1px solid #1e3d00; border-radius: 2px; padding: 0.1rem 0.4rem; white-space: nowrap; }

  .btn-dismiss { background: none; border: none; color: #2a5200; cursor: pointer;
    font-size: 0.8rem; padding: 0; line-height: 1; transition: color 0.15s; }
  .btn-dismiss:hover { color: #6db800; }

  .spinner { width: 14px; height: 14px; flex-shrink: 0; border: 2px solid #1e3d00;
    border-top-color: #6db800; border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
</style>
